import { useState, useRef, useEffect } from 'react';
import { Activity, X, Plus, ChevronRight } from 'lucide-react';
import { useStudioStore } from '../../store/useStudioStore';
import * as Tone from 'tone';

interface TempoTrackProps {
  pixelsPerBeat?: number;
  totalBeats?: number;
  currentTime?: number; // in beats
  onSeek?: (time: number) => void;
}

export const TempoTrack: React.FC<TempoTrackProps> = ({
  pixelsPerBeat = 100,
  totalBeats = 64,
  currentTime = 0,
  onSeek,
}) => {
  const { tempoPoints, bpm, addTempoPoint, removeTempoPoint, updateTempoPoint } = useStudioStore();
  const [draggingPoint, setDraggingPoint] = useState<{ time: number; startY: number; startBpm: number } | null>(null);
  const [showTempoList, setShowTempoList] = useState(false);
  const trackRef = useRef<HTMLDivElement>(null);
  const graphRef = useRef<HTMLCanvasElement>(null);

  // Constants for graph
  const MIN_BPM = 60;
  const MAX_BPM = 200;
  const GRAPH_HEIGHT = 80;
  const POINT_RADIUS = 6;

  // Draw tempo automation curve
  useEffect(() => {
    const canvas = graphRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Set canvas size
    canvas.width = totalBeats * pixelsPerBeat;
    canvas.height = GRAPH_HEIGHT;

    // Clear canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Draw grid lines (every 4 beats)
    ctx.strokeStyle = '#2a2a2a';
    ctx.lineWidth = 1;
    for (let i = 0; i <= Math.ceil(totalBeats / 4); i++) {
      const x = i * 4 * pixelsPerBeat;
      ctx.beginPath();
      ctx.moveTo(x, 0);
      ctx.lineTo(x, GRAPH_HEIGHT);
      ctx.stroke();
    }

    // Draw BPM grid lines (every 20 BPM)
    ctx.strokeStyle = '#1a1a1a';
    ctx.lineWidth = 1;
    for (let bpmValue = MIN_BPM; bpmValue <= MAX_BPM; bpmValue += 20) {
      const y = GRAPH_HEIGHT - ((bpmValue - MIN_BPM) / (MAX_BPM - MIN_BPM)) * GRAPH_HEIGHT;
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(canvas.width, y);
      ctx.stroke();

      // Draw BPM label
      ctx.fillStyle = '#666';
      ctx.font = '10px monospace';
      ctx.fillText(`${bpmValue}`, 5, y - 2);
    }

    // If no tempo points, draw flat line at current BPM
    if (tempoPoints.length === 0) {
      const y = GRAPH_HEIGHT - ((bpm - MIN_BPM) / (MAX_BPM - MIN_BPM)) * GRAPH_HEIGHT;
      ctx.strokeStyle = '#6366f1';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(canvas.width, y);
      ctx.stroke();
      return;
    }

    // Draw tempo automation curve
    ctx.strokeStyle = '#6366f1';
    ctx.lineWidth = 2;
    ctx.beginPath();

    // Start from beginning
    const firstPoint = tempoPoints[0];
    const firstY = GRAPH_HEIGHT - ((firstPoint.bpm - MIN_BPM) / (MAX_BPM - MIN_BPM)) * GRAPH_HEIGHT;
    ctx.moveTo(0, firstY);

    // Draw line to first point
    const firstX = firstPoint.time * pixelsPerBeat;
    ctx.lineTo(firstX, firstY);

    // Draw lines between points
    for (let i = 0; i < tempoPoints.length - 1; i++) {
      const point = tempoPoints[i];
      const nextPoint = tempoPoints[i + 1];

      const x1 = point.time * pixelsPerBeat;
      const y1 = GRAPH_HEIGHT - ((point.bpm - MIN_BPM) / (MAX_BPM - MIN_BPM)) * GRAPH_HEIGHT;
      const x2 = nextPoint.time * pixelsPerBeat;
      const y2 = GRAPH_HEIGHT - ((nextPoint.bpm - MIN_BPM) / (MAX_BPM - MIN_BPM)) * GRAPH_HEIGHT;

      ctx.lineTo(x2, y2);
    }

    // Draw line to end
    const lastPoint = tempoPoints[tempoPoints.length - 1];
    const lastY = GRAPH_HEIGHT - ((lastPoint.bpm - MIN_BPM) / (MAX_BPM - MIN_BPM)) * GRAPH_HEIGHT;
    ctx.lineTo(canvas.width, lastY);

    ctx.stroke();

    // Draw tempo points
    tempoPoints.forEach((point) => {
      const x = point.time * pixelsPerBeat;
      const y = GRAPH_HEIGHT - ((point.bpm - MIN_BPM) / (MAX_BPM - MIN_BPM)) * GRAPH_HEIGHT;

      // Draw point circle
      ctx.fillStyle = '#6366f1';
      ctx.beginPath();
      ctx.arc(x, y, POINT_RADIUS, 0, Math.PI * 2);
      ctx.fill();

      // Draw point border
      ctx.strokeStyle = '#1a1a1a';
      ctx.lineWidth = 2;
      ctx.stroke();

      // Draw BPM label
      ctx.fillStyle = '#ffffff';
      ctx.font = '11px monospace';
      ctx.textAlign = 'center';
      ctx.fillText(`${Math.round(point.bpm)}`, x, y - 12);
    });
  }, [tempoPoints, bpm, pixelsPerBeat, totalBeats]);

  const handleTrackClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!trackRef.current) return;

    const rect = trackRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    const time = x / pixelsPerBeat;
    const bpmValue = MIN_BPM + ((GRAPH_HEIGHT - y) / GRAPH_HEIGHT) * (MAX_BPM - MIN_BPM);

    // Check if clicking near existing point (within 10px)
    const clickedPoint = tempoPoints.find((p) => {
      const pointX = p.time * pixelsPerBeat;
      const pointY = GRAPH_HEIGHT - ((p.bpm - MIN_BPM) / (MAX_BPM - MIN_BPM)) * GRAPH_HEIGHT;
      const distance = Math.sqrt(Math.pow(pointX - x, 2) + Math.pow(pointY - y, 2));
      return distance < 10;
    });

    if (clickedPoint) {
      // Start dragging point
      setDraggingPoint({
        time: clickedPoint.time,
        startY: y,
        startBpm: clickedPoint.bpm,
      });
    } else {
      // Create new tempo point
      addTempoPoint({
        time: Math.max(0, Math.min(time, totalBeats)),
        bpm: Math.max(MIN_BPM, Math.min(MAX_BPM, Math.round(bpmValue))),
      });
    }
  };

  const handleContextMenu = (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (!trackRef.current) return;

    const rect = trackRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    // Check if right-clicking near existing point (within 10px)
    const clickedPoint = tempoPoints.find((p) => {
      const pointX = p.time * pixelsPerBeat;
      const pointY = GRAPH_HEIGHT - ((p.bpm - MIN_BPM) / (MAX_BPM - MIN_BPM)) * GRAPH_HEIGHT;
      const distance = Math.sqrt(Math.pow(pointX - x, 2) + Math.pow(pointY - y, 2));
      return distance < 10;
    });

    if (clickedPoint) {
      removeTempoPoint(clickedPoint.time);
    }
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!draggingPoint || !trackRef.current) return;

    const rect = trackRef.current.getBoundingClientRect();
    const y = e.clientY - rect.top;

    const deltaY = draggingPoint.startY - y;
    const deltaBpm = (deltaY / GRAPH_HEIGHT) * (MAX_BPM - MIN_BPM);
    const newBpm = Math.max(MIN_BPM, Math.min(MAX_BPM, Math.round(draggingPoint.startBpm + deltaBpm)));

    updateTempoPoint(draggingPoint.time, newBpm);
  };

  const handleMouseUp = () => {
    setDraggingPoint(null);
  };

  const handleDeletePoint = (time: number, e: React.MouseEvent) => {
    e.stopPropagation();
    removeTempoPoint(time);
  };

  // Handle mouse up globally
  useEffect(() => {
    if (draggingPoint) {
      window.addEventListener('mouseup', handleMouseUp);
      return () => window.removeEventListener('mouseup', handleMouseUp);
    }
  }, [draggingPoint]);

  return (
    <div className="flex flex-col">
      {/* Tempo Track Lane */}
      <div className="relative bg-[#1a1a1a] border-b border-[#2a2a2a]" style={{ height: GRAPH_HEIGHT + 32 }}>
        {/* Track Label */}
        <div className="absolute left-0 top-0 h-full w-32 bg-[#1f1f1f] border-r border-[#2a2a2a] flex flex-col justify-between p-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Activity size={14} className="text-[#6366f1]" />
              <span className="text-xs font-medium">Tempo</span>
            </div>
            <button
              onClick={() => setShowTempoList(!showTempoList)}
              className="p-1 hover:bg-[#2a2a2a] rounded transition-colors"
              title="Toggle tempo list"
            >
              <ChevronRight
                size={12}
                className={`transition-transform ${showTempoList ? 'rotate-90' : ''}`}
              />
            </button>
          </div>
          <div className="text-[10px] text-zinc-500">
            <div>Min: {MIN_BPM} BPM</div>
            <div>Max: {MAX_BPM} BPM</div>
          </div>
        </div>

        {/* Tempo Graph */}
        <div
          ref={trackRef}
          className="absolute left-32 right-0 top-8 cursor-crosshair"
          style={{ width: `${totalBeats * pixelsPerBeat}px`, height: GRAPH_HEIGHT }}
          onClick={handleTrackClick}
          onMouseMove={handleMouseMove}
          onContextMenu={handleContextMenu}
        >
          <canvas
            ref={graphRef}
            className="absolute top-0 left-0"
          />

          {/* Playhead indicator */}
          {currentTime !== undefined && (
            <div
              className="absolute top-0 bottom-0 w-0.5 bg-white pointer-events-none"
              style={{ left: `${currentTime * pixelsPerBeat}px` }}
            />
          )}
        </div>

        {/* Instructions */}
        <div className="absolute left-32 top-1 text-[10px] text-zinc-500">
          Click to add tempo point • Drag point to adjust BPM • Right-click to delete
        </div>
      </div>

      {/* Tempo Point List */}
      {showTempoList && (
        <div className="bg-[#1f1f1f] border-b border-[#2a2a2a] p-3">
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-xs font-semibold">Tempo Points</h4>
            <span className="text-[10px] text-zinc-500">
              {tempoPoints.length} point{tempoPoints.length !== 1 ? 's' : ''}
            </span>
          </div>

          {tempoPoints.length === 0 ? (
            <p className="text-xs text-zinc-500 italic">
              Click on the tempo track to create tempo points
            </p>
          ) : (
            <div className="space-y-1 max-h-40 overflow-y-auto">
              {tempoPoints.map((point, index) => (
                <div
                  key={`${point.time}-${index}`}
                  className="flex items-center gap-2 p-2 hover:bg-[#2a2a2a] rounded cursor-pointer group"
                  onClick={() => onSeek && onSeek(point.time)}
                >
                  <div className="w-3 h-3 rounded-full bg-[#6366f1] flex-shrink-0" />
                  <span className="text-xs flex-1">
                    {point.time.toFixed(1)} beats
                  </span>
                  <span className="text-xs font-mono text-[#6366f1]">
                    {Math.round(point.bpm)} BPM
                  </span>
                  <button
                    onClick={(e) => handleDeletePoint(point.time, e)}
                    className="opacity-0 group-hover:opacity-100 p-1 hover:bg-red-600 rounded transition-all"
                  >
                    <X size={10} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
